import React from 'react'
import DOMPurify from 'dompurify'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type {
  NotificationContentBlock,
  NotificationDisplayType,
} from '@library/api-menu-experience'

const Message = withCitrusTheme(Box, (theme) => ({
  '& a': {
    color: theme.color(Color.Secondary_400),
    fontWeight: FontWeight.Bold,
  },
  '& p': {
    margin: 0,
  },
}))

const notificationStyles = {
  note: {
    bg: Color.ColdGrey_50,
    borderColor: Color.ColdGrey_200,
    accentColor: Color.ColdGrey_900,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  info: {
    bg: Color.Informative_50,
    borderColor: Color.Informative_900,
    accentColor: Color.Informative_900,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  warning: {
    bg: Color.Warning_50,
    borderColor: Color.Warning_100,
    accentColor: Color.Warning_900,
    iconName: 'warning',
    iconVariant: IconVariant.Inherit,
  },
  error: {
    bg: Color.Error_50,
    borderColor: Color.Error_100,
    accentColor: Color.Error_900,
    iconName: 'error',
    iconVariant: IconVariant.Error,
  },
  success: {
    bg: Color.Success_50,
    borderColor: Color.Success_900,
    accentColor: Color.Success_900,
    iconName: 'success',
    iconVariant: IconVariant.Confirmation,
  },
} as const

const getDisplayType = (displayType: string): NotificationDisplayType => {
  if (displayType in notificationStyles) {
    return displayType as NotificationDisplayType
  }

  return 'info'
}

type NotificationProps = {
  contentBlock: NotificationContentBlock
}

export const Notification = ({ contentBlock }: NotificationProps) => {
  const displayType = getDisplayType(contentBlock.displayType)
  const styles = notificationStyles[displayType]

  return (
    <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
      <Box
        data-testid={`notification-${displayType}`}
        bg={styles.bg}
        borderColor={styles.borderColor}
        borderWidth={0.5}
        borderStyle={BorderStyle.Solid}
        maxWidth="1540px"
        paddingH={4}
        paddingV={4}
        width="100%"
      >
        <Box display={Display.Flex} gap={2}>
          <Box color={styles.accentColor}>
            <Icon name={styles.iconName} variant={styles.iconVariant} />
          </Box>

          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={2}
            alignItems={AlignItems.FlexStart}
          >
            <Text color={styles.accentColor} fontWeight={FontWeight.Bold}>
              {contentBlock.title}
            </Text>

            <Message
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contentBlock.messageHTML),
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
